/**
 * Subscription Utility Functions
 *
 * Utilities for checking subscription status, expiration dates, and renewal requirements
 *
 * @package @ideoz/core
 */

/**
 * Check if a subscription expires within the specified number of days
 * 
 * @param expirationDate - The subscription expiration date
 * @param daysThreshold - Number of days before expiration to check (default: 1)
 * @returns true if subscription expires within the threshold, false otherwise
 */
export function isSubscriptionExpiringWithinDays(
  expirationDate: string | Date,
  daysThreshold: number = 1
): boolean {
  const expDate = typeof expirationDate === 'string' ? new Date(expirationDate) : expirationDate;
  const now = new Date();
  
  // Reset time to midnight for accurate day comparison
  const expDateMidnight = new Date(expDate.getFullYear(), expDate.getMonth(), expDate.getDate());
  const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // Calculate difference in days
  const diffTime = expDateMidnight.getTime() - nowMidnight.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  // Check if within threshold (including exact day)
  return diffDays <= daysThreshold && diffDays >= 0;
}

/**
 * Check if a subscription has already expired
 * 
 * @param expirationDate - The subscription expiration date
 * @returns true if subscription has expired, false otherwise
 */
export function isSubscriptionExpired(expirationDate: string | Date): boolean {
  const expDate = typeof expirationDate === 'string' ? new Date(expirationDate) : expirationDate;
  const now = new Date();
  
  return now > expDate;
}

/**
 * Format days until expiration
 * 
 * @param expirationDate - The subscription expiration date
 * @returns Formatted string like "1 day" or "2 days"
 */
export function getDaysUntilExpiration(expirationDate: string | Date): string {
  const expDate = typeof expirationDate === 'string' ? new Date(expirationDate) : expirationDate;
  const now = new Date();
  
  const expDateMidnight = new Date(expDate.getFullYear(), expDate.getMonth(), expDate.getDate());
  const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  const diffTime = expDateMidnight.getTime() - nowMidnight.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 1) {
    return '1 day';
  }
  
  return `${diffDays} days`;
}
